import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Button, Spinner } from "reactstrap";
import { loadGoogleMapsApi, isGoogleMapsLoaded, waitForGoogleMapsToLoad } from "./GoogleMapLoader";

const GoogleMapWrapper = ({ 
  apiKey, 
  center, 
  zoom = 15, 
  markerPosition, 
  onClick, 
  onMapLoad,
  height = "200px"
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [mapLoadError, setMapLoadError] = useState(false);
  const [loadRetries, setLoadRetries] = useState(0);
  const mapRef = useRef(null);
  const MAX_RETRIES = 3;

  const mapContainerStyle = {
    width: "100%",
    height: height,
  };

  // Función para verificar si la API key está definida
  const isApiKeyDefined = () => {
    return apiKey && apiKey.length > 10;
  };

  // Cargar la API de Google Maps
  useEffect(() => {
    if (!isApiKeyDefined()) {
      return;
    }

    const loadApi = async () => {
      try {
        setIsLoading(true);
        
        // Cargar la API de Google Maps
        await loadGoogleMapsApi(apiKey, ['places', 'geometry']);
        
        // Esperar a que la API esté completamente cargada
        await waitForGoogleMapsToLoad();
        
        setIsLoading(false);
        setMapLoadError(false);
      } catch (error) {
        console.error("Error al cargar Google Maps API:", error);
        setMapLoadError(true);
        setIsLoading(false);
      }
    };

    loadApi();
  }, [apiKey, loadRetries]);

  const retryLoadMap = () => {
    if (loadRetries < MAX_RETRIES) {
      //console.log("Reintentando cargar el mapa de Google...");
      setMapLoadError(false);
      setLoadRetries((prev) => prev + 1);
    }
  };

  const handleMapLoad = (map) => {
    mapRef.current = map;
    if (onMapLoad && map) {
      onMapLoad(map);
    }
  };

  if (!isApiKeyDefined()) {
    return (
      <div className="text-center p-3 bg-light rounded">
        <p className="text-danger mb-2">
          <i className="ri-error-warning-line me-1"></i>
          Error: La clave de API de Google Maps no está configurada correctamente.
        </p>
        <small className="text-muted">
          Por favor, configure la variable de entorno REACT_APP_APIKEYGOOGLEMAPS.
        </small>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center p-3 bg-light rounded">
        <Spinner color="primary" />
        <p className="mt-2 mb-0">Cargando Google Maps...</p>
      </div>
    );
  }

  if (mapLoadError) {
    return (
      <div className="text-center p-3 bg-light rounded">
        <p className="text-danger mb-2">
          <i className="ri-error-warning-line me-1"></i>
          Error al cargar el mapa de Google
        </p>
        <Button color="primary" size="sm" onClick={retryLoadMap}>
          <i className="ri-refresh-line me-1"></i>
          Reintentar cargar el mapa
        </Button>
      </div>
    );
  }

  return (
    <div className="google-map-container">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={zoom}
        onClick={onClick}
        onLoad={handleMapLoad}
      >
        {markerPosition && (
          <Marker
            position={{
              lat: markerPosition.lat || markerPosition[0],
              lng: markerPosition.lng || markerPosition[1],
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default GoogleMapWrapper;
